import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";

import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Collapse,
  Tooltip,
  ButtonBase,
  CircularProgress,
} from "@mui/material";

import FolderRounded from "@mui/icons-material/FolderRounded";
import LayersOutlined from "@mui/icons-material/LayersOutlined";
import SearchRounded from "@mui/icons-material/SearchRounded";
import SchoolOutlined from "@mui/icons-material/SchoolOutlined";
import AccountBalanceOutlined from "@mui/icons-material/AccountBalanceOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRounded from "@mui/icons-material/KeyboardArrowUpRounded";
import PersonOutlineRounded from "@mui/icons-material/PersonOutlineRounded";
import PersonOffOutlined from "@mui/icons-material/PersonOffOutlined";
import PersonRounded from "@mui/icons-material/PersonRounded";
import SwapHorizRounded from "@mui/icons-material/SwapHorizRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import PeopleOutlineRounded from "@mui/icons-material/PeopleOutlineRounded";

import { useNavigate } from "react-router-dom";
import { api } from "@api/api";
import { CondicaoContext } from "../../../contexto";

import {
  coresTextosGlobais,
  tamanhosPaginasAdministrativas as pagina,
  tamanhosControlesGlobais as controle,
} from "../../../estilos/global";

import {
  agruparInstituicoes,
  calcularIndicadoresProcessos,
  categoriasProcessos,
  categoriaValida,
  filtrarInstituicoes,
  responsavelInstituicao,
} from "./modeloListaProcessos";

const azul = "var(--sear-azul, #087cf0)",
  texto = "var(--sear-texto, #365d90)",
  secundario = "var(--sear-texto-secundario, #71829d)",
  borda = "var(--sear-borda, #d8e9fb)",
  suave = "var(--sear-superficie-azul, #f4f9ff)";

const botoes = {
  textTransform: "none",
  borderRadius: 1,
  borderColor: borda,
  color: azul,
  bgcolor: "transparent",
  boxShadow: "none",
  fontSize: controle.botao,
  whiteSpace: "nowrap",
  "&:hover,&.Mui-focusVisible": {
    bgcolor: "var(--sear-acao-azul, #0767d8)",
    borderColor: "var(--sear-acao-azul, #0767d8)",
    color: "#fff",
  },
};

const foco = {
  "&.Mui-focusVisible": {
    outline: `2px solid ${azul}`,
    outlineOffset: -2,
  },
};

const nomesAtribuicao = {
  all: "Todas",
  unassigned: "Sem atribuição",
  mine: "Atribuídas a mim",
  others: "Atribuídas a outros",
};

function lerEstado(userId) {
  let busca = {};
  let filtros = {};

  if (userId) {
    try {
      busca =
        JSON.parse(
          sessionStorage.getItem(`processosAnalistaLastSearch_${userId}`) ||
            "{}",
        ) || {};
    } catch {}

    try {
      filtros =
        JSON.parse(
          localStorage.getItem(`processosAnalistaFilters_${userId}`) || "{}",
        ) || {};
    } catch {}
  }

  return {
    searchTerm: typeof busca.searchTerm === "string" ? busca.searchTerm : "",

    expandedSchool:
      typeof busca.expandedSchool === "string" ? busca.expandedSchool : null,

    categoria:
      busca.categoria === "anterior"
        ? "todas"
        : categoriaValida(busca.categoria),

    modoAberto: busca.modo !== "finished",

    filtros: {
      open: ["all", "unassigned", "mine", "others"].includes(filtros.open)
        ? filtros.open
        : "all",

      finished: ["all", "mine", "others"].includes(filtros.finished)
        ? filtros.finished
        : "all",
    },
  };
}

function Responsavel({ responsavel }) {
  const Icone = responsavel.meu
    ? PersonRounded
    : responsavel.atribuido
      ? PersonOutlineRounded
      : PersonOffOutlined;

  return (
    <Tooltip
      title={responsavel.rotulo}
      arrow
      describeChild
      disableFocusListener
    >
      <Box
        component="span"
        role="img"
        aria-label={responsavel.rotulo}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 30,
          height: 30,
          flexShrink: 0,
          color: responsavel.meu ? azul : secundario,
        }}
      >
        <Icone aria-hidden="true" sx={{ fontSize: 19 }} />
      </Box>
    </Tooltip>
  );
}

function IndicadorProcessos({ titulo, valor, Icone }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        minWidth: 0,
        minHeight: 56,
        px: 1.5,
        py: 0.8,
        border: "1px solid transparent",
        borderRadius: 1.5,
        background: "linear-gradient(120deg, #087cf0 0%, #39adff 100%)",
        color: "#fff",
      }}
    >
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: 1.5,
          bgcolor: "rgba(255,255,255,.17)",
          color: "#fff",
        }}
      >
        <Icone sx={{ fontSize: 23 }} aria-hidden="true" />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: controle.rotulo,
            lineHeight: 1.25,
            color: "#fff",
          }}
        >
          {titulo}
        </Typography>

        <Typography
          sx={{
            fontSize: 22,
            lineHeight: 1.15,
            fontWeight: 700,
            color: "#fff",
          }}
        >
          {valor}
        </Typography>
      </Box>
    </Box>
  );
}

function ListaProcessos({ userId }) {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const [inicial] = useState(() => lerEstado(userId));

  const [searchTerm, setSearchTerm] = useState(inicial.searchTerm);

  const [expandedSchool, setExpandedSchool] = useState(inicial.expandedSchool);

  const [categoria, setCategoria] = useState(inicial.categoria);

  const [modoAberto, setModoAberto] = useState(inicial.modoAberto);

  const [assignmentFilters, setAssignmentFilters] = useState(inicial.filtros);

  const [indicadores, setIndicadores] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const modoAtualRef = useRef(modoAberto);
  const consultaProcessosRef = useRef(null);

  modoAtualRef.current = modoAberto;

  const currentModeKey = modoAberto ? "open" : "finished";
  const assignmentFilter = assignmentFilters[currentModeKey];

  const opcoes = modoAberto
    ? ["all", "unassigned", "mine", "others"]
    : ["all", "mine", "others"];

  useEffect(() => {
    if (!userId) return;

    try {
      sessionStorage.setItem(
        `processosAnalistaLastSearch_${userId}`,
        JSON.stringify({
          searchTerm,
          expandedSchool,
          categoria,
          modo: currentModeKey,
        }),
      );
    } catch {}
  }, [userId, searchTerm, expandedSchool, categoria, currentModeKey]);

  useEffect(() => {
    if (!userId) return;

    try {
      localStorage.setItem(
        `processosAnalistaFilters_${userId}`,
        JSON.stringify(assignmentFilters),
      );
    } catch {}
  }, [userId, assignmentFilters]);

  useEffect(() => {
    const recolher = (event) => {
      const painel = containerRef.current;

      if (!painel || !(event.target instanceof Element)) {
        return;
      }

      if (
        !painel.contains(event.target) ||
        !event.target.closest("[data-instituicao],[data-processo]")
      ) {
        setExpandedSchool(null);
      }
    };

    document.addEventListener("mousedown", recolher);

    return () => {
      document.removeEventListener("mousedown", recolher);
    };
  }, []);

  const fetchProcesses = useCallback(() => {
    consultaProcessosRef.current?.abort();

    const controller = new AbortController();
    consultaProcessosRef.current = controller;

    const consultaAtiva = () =>
      consultaProcessosRef.current === controller &&
      !controller.signal.aborted &&
      modoAtualRef.current === modoAberto;

    setLoading(true);
    setError(null);

    const endpoint = modoAberto ? api.allProcAna() : api.AllProcFini();

    return fetch(endpoint, {
      method: "GET",
      credentials: "include",
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Erro ao carregar processos");
        }

        return res.json();
      })
      .then((data) => {
        if (!consultaAtiva()) return;

        if (!Array.isArray(data)) {
          throw new Error("Resposta inválida da API");
        }

        setSchools(agruparInstituicoes(data));

        if (modoAberto) {
          setIndicadores(calcularIndicadoresProcessos(data));
        }
      })
      .catch((error) => {
        if (!consultaAtiva()) return;

        console.error("Erro ao buscar processos:", error);
        setError(error.message);
      })
      .finally(() => {
        if (consultaAtiva()) {
          setLoading(false);
        }
      });
  }, [modoAberto]);

  useEffect(() => {
    fetchProcesses();

    return () => {
      consultaProcessosRef.current?.abort();
      consultaProcessosRef.current = null;
    };
  }, [fetchProcesses]);

  const handleToggleMode = () => {
    setLoading(true);
    setError(null);
    setModoAberto((anterior) => !anterior);
    setExpandedSchool(null);
  };

  const handleAssignmentFilterChange = (valor) => {
    setAssignmentFilters((anterior) => ({
      ...anterior,
      [currentModeKey]: valor,
    }));

    setExpandedSchool(null);
  };

  const lista = filtrarInstituicoes(
    schools,
    searchTerm,
    categoria,
    assignmentFilter,
  );

  // Preserva a expansão salva por versões anteriores.
  const expandidaId = schools.some(
    (instituicao) => instituicao.id === expandedSchool,
  )
    ? expandedSchool
    : schools.filter((instituicao) => instituicao.name === expandedSchool)
          .length === 1
      ? schools.find((instituicao) => instituicao.name === expandedSchool).id
      : null;

  const limpar = () => {
    setSearchTerm("");
    setCategoria("todas");
    handleAssignmentFilterChange("all");
  };

  return (
    <Box
      data-modo={modoAberto ? "abertos" : "finalizados"}
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "1 0 auto",
        minHeight: "auto",
        gap: 1.25,
      }}
    >
      {/* CABEÇALHO */}
      <Box
        component="header"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.4,
          mb: 0.5,
          flexShrink: 0,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            display: "grid",
            placeItems: "center",
            bgcolor: suave,
            borderRadius: 1.5,
            color: azul,
            flexShrink: 0,
          }}
        >
          <FolderRounded sx={{ fontSize: 27 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography
            component="h1"
            sx={{
              fontSize: pagina.tituloPagina,
              fontWeight: 700,
              lineHeight: 1.15,
              color: coresTextosGlobais.tituloPagina,
            }}
          >
            {modoAberto ? "Processos abertos" : "Processos finalizados"}
          </Typography>

          <Typography
            sx={{
              mt: 0.3,
              fontSize: controle.descricao,
              lineHeight: 1.5,
              color: secundario,
            }}
          >
            {modoAberto
              ? "Consulte e acompanhe os processos em andamento."
              : "Consulte os processos finalizados."}
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleToggleMode}
          startIcon={<SwapHorizRounded />}
          sx={{
            ...botoes,
            bgcolor: azul,
            color: "#fff",
            fontWeight: 600,
            px: 2,
            py: 1,
            boxShadow: "0 2px 5px rgba(8,124,240,.16)",
          }}
        >
          {modoAberto ? "Ver finalizados" : "Ver abertos"}
        </Button>
      </Box>

      <Box
        ref={containerRef}
        component="section"
        aria-label={`Instituições com processos ${
          modoAberto ? "abertos" : "finalizados"
        }`}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: "1 0 auto",
          minHeight: "auto",
          gap: 1.25,
        }}
      >
        {/* CINCO INDICADORES */}
        {modoAberto && (
          <Box
            component="section"
            role="group"
            aria-label="Indicadores de processos"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0,1fr)",
                sm: "repeat(2,minmax(0,1fr))",
                md: "repeat(5,minmax(0,1fr))",
              },
              gap: 1.25,
              flexShrink: 0,
            }}
          >
            {[
              {
                titulo: "Total de processos",
                valor: indicadores?.total,
                Icone: LayersOutlined,
              },
              {
                titulo: "Escolas",
                valor: indicadores?.escolas,
                Icone: SchoolOutlined,
              },
              {
                titulo: "Mantenedoras",
                valor: indicadores?.mantenedoras,
                Icone: AccountBalanceOutlined,
              },
              {
                titulo: "Atribuídas a mim",
                valor: indicadores?.meus,
                Icone: PersonOutlineRounded,
              },
              {
                titulo: "Sem atribuição",
                valor: indicadores?.semAtribuicao,
                Icone: PersonOffOutlined,
              },
            ].map(({ titulo, valor, Icone }) => (
              <IndicadorProcessos
                key={titulo}
                titulo={titulo}
                valor={loading || error ? "—" : (valor ?? "—")}
                Icone={Icone}
              />
            ))}
          </Box>
        )}

        {/* PRIMEIRA LINHA: INSTITUIÇÕES E CATEGORIAS */}
        <Box
          component="section"
          aria-label="Busca e filtros"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "auto minmax(0, 1fr)",
            },
            alignItems: "center",
            columnGap: 2,
            rowGap: 1.25,
            pt: 1.25,
            pb: 1.25,
            borderBottom: `1px solid ${borda}`,
            flexShrink: 0,
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: pagina.tituloSecao,
              color: coresTextosGlobais.tituloSecao,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            Instituições
          </Typography>

          <Box
            role="group"
            aria-label="Filtrar por instituição"
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.5,
              p: 0.5,
              bgcolor: suave,
              borderRadius: 1.5,
              width: "fit-content",
              maxWidth: "100%",
            }}
          >
            {[
              {
                valor: "todas",
                nome: "Todas",
                Icone: null,
              },
              {
                valor: "escola",
                nome: "Escolas",
                Icone: SchoolOutlined,
              },
              {
                valor: "mantenedora",
                nome: "Mantenedoras",
                Icone: AccountBalanceOutlined,
              },
            ].map(({ valor, nome, Icone }) => (
              <Button
                key={valor}
                aria-pressed={categoria === valor}
                onClick={() => {
                  setCategoria(valor);
                  setExpandedSchool(null);
                }}
                startIcon={Icone ? <Icone sx={{ fontSize: 18 }} /> : undefined}
                sx={{
                  ...botoes,
                  px: 1.3,
                  py: 0.65,
                  ...(categoria === valor
                    ? {
                        bgcolor: "var(--sear-superficie, #fff)",
                        color: azul,
                        fontWeight: 600,
                        boxShadow: "0 1px 4px rgba(27,71,115,.12)",
                      }
                    : {
                        color: secundario,
                      }),
                }}
              >
                {nome}
              </Button>
            ))}
          </Box>
        </Box>

        {/* SEGUNDA LINHA: BUSCA À ESQUERDA E ATRIBUIÇÕES CENTRALIZADAS */}
        <Box
          component="section"
          aria-label="Filtros de atribuição"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              lg: "minmax(0, 1fr) auto minmax(0, 1fr)",
            },
            alignItems: "center",
            gap: 1.25,
            py: 0.5,
            flexShrink: 0,
          }}
        >
          <TextField
            size="small"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Pesquisar instituição, processo ou analista"
            inputProps={{
              "aria-label": "Pesquisar instituição, processo ou analista",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded
                    sx={{
                      fontSize: 19,
                      color: azul,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "100%",
              maxWidth: 340,
              minWidth: 0,
              justifySelf: "start",
              "& input": {
                fontSize: controle.campoBusca,
              },
              "& fieldset": {
                borderColor: borda,
              },
            }}
          />

          <Box
            role="group"
            aria-label="Atalhos de atribuição"
            sx={{
              gridColumn: {
                xs: "1",
                lg: "2",
              },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              flexWrap: "wrap",
            }}
          >
            {opcoes.map((valor) => {
              const Icone =
                valor === "all"
                  ? CheckRounded
                  : valor === "unassigned"
                    ? PersonOffOutlined
                    : valor === "mine"
                      ? PersonRounded
                      : PeopleOutlineRounded;

              const selecionado = assignmentFilter === valor;

              return (
                <Button
                  key={valor}
                  variant="outlined"
                  size="small"
                  startIcon={<Icone sx={{ fontSize: 17 }} />}
                  aria-pressed={selecionado}
                  onClick={() => handleAssignmentFilterChange(valor)}
                  sx={{
                    ...botoes,
                    borderRadius: "999px",
                    py: 0.35,
                    px: 1.5,
                    borderColor: selecionado ? azul : borda,
                    bgcolor: selecionado ? azul : "#fff",
                    color: selecionado ? "#fff" : azul,
                    fontWeight: selecionado ? 600 : 500,
                    boxShadow: selecionado
                      ? "0 2px 5px rgba(8,124,240,0.18)"
                      : "none",
                    "& .MuiButton-startIcon": {
                      mr: 0.5,
                    },
                    "&:hover": {
                      bgcolor: selecionado ? "#0767d8" : suave,
                      borderColor: azul,
                      color: selecionado ? "#fff" : azul,
                    },
                  }}
                >
                  {nomesAtribuicao[valor]}
                </Button>
              );
            })}
          </Box>

          {!loading && !error && (
            <Typography
              role="status"
              sx={{
                gridColumn: {
                  xs: "1",
                  lg: "3",
                },
                justifySelf: "end",
                fontSize: controle.contador,
                color: secundario,
                whiteSpace: "nowrap",
              }}
            >
              {lista.length}{" "}
              {lista.length === 1 ? "instituição" : "instituições"}
            </Typography>
          )}
        </Box>

        {/* LISTA DE INSTITUIÇÕES */}
        <Box
          sx={{
            overflowY: "visible",
            flex: "1 0 auto",
            minHeight: "auto",
            border: `1px solid ${borda}`,
            borderRadius: 1.5,
          }}
        >
          {loading ? (
            <Box
              sx={{
                p: 4,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={24} aria-label="Carregando processos" />
            </Box>
          ) : error ? (
            <Box
              role="alert"
              sx={{
                p: 4,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  color: "error.main",
                  mb: 1,
                }}
              >
                Ocorreu um erro ao carregar os processos
              </Typography>

              <Typography
                sx={{
                  color: secundario,
                  mb: 2,
                }}
              >
                {error}
              </Typography>

              <Button variant="outlined" sx={botoes} onClick={fetchProcesses}>
                Tentar Novamente
              </Button>
            </Box>
          ) : lista.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  color: secundario,
                  fontSize: controle.descricao,
                  mb: 2,
                }}
              >
                {schools.length === 0
                  ? `Nenhum processo ${modoAberto ? "aberto" : "finalizado"}`
                  : "Nenhum processo corresponde aos filtros."}
              </Typography>

              <Button
                variant="outlined"
                sx={botoes}
                onClick={schools.length ? limpar : fetchProcesses}
              >
                {schools.length ? "Limpar filtros" : "Recarregar"}
              </Button>
            </Box>
          ) : (
            <Box
              component="ul"
              sx={{
                m: 0,
                p: 0,
                listStyle: "none",
              }}
            >
              {lista.map((instituicao) => {
                const expandida = expandidaId === instituicao.id;

                const Icone =
                  instituicao.categoria === "escola"
                    ? SchoolOutlined
                    : AccountBalanceOutlined;

                const responsavel = responsavelInstituicao(instituicao);

                return (
                  <Box
                    component="li"
                    key={instituicao.id}
                    sx={{
                      borderBottom: `1px solid ${borda}`,
                      bgcolor: "var(--sear-superficie, #fff)",
                    }}
                  >
                    <Tooltip
                      title={responsavel.rotulo}
                      arrow
                      describeChild
                      disableHoverListener
                      disableTouchListener
                    >
                      <ButtonBase
                        disableRipple
                        data-instituicao={instituicao.id}
                        aria-expanded={expandida}
                        aria-label={`${
                          expandida ? "Recolher" : "Ver processos de"
                        } ${instituicao.name}, ${
                          categoriasProcessos[instituicao.categoria]
                        }`}
                        onClick={() =>
                          setExpandedSchool(expandida ? null : instituicao.id)
                        }
                        sx={{
                          ...foco,
                          width: "100%",
                          display: {
                            xs: "grid",
                            md: "flex",
                          },
                          gridTemplateColumns: "36px minmax(0,1fr) 21px",
                          alignItems: "center",
                          textAlign: "left",
                          gap: {
                            xs: 1,
                            md: 1.5,
                          },
                          px: 1.75,
                          py: 1.1,
                          "& > svg:last-child": {
                            gridColumn: 3,
                            gridRow: "1 / 3",
                          },
                          color: texto,
                          borderLeft: `3px solid ${
                            expandida ? azul : "transparent"
                          }`,
                          "&:hover": {
                            bgcolor: suave,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            gridColumn: 1,
                            gridRow: "1 / 3",
                            display: "grid",
                            placeItems: "center",
                            borderRadius: 1,
                            bgcolor: suave,
                            flexShrink: 0,
                          }}
                        >
                          <Icone
                            sx={{
                              fontSize: 23,
                              color: azul,
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            gridColumn: 2,
                            gridRow: 1,
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              display: "block",
                              fontSize: controle.descricao,
                              fontWeight: 600,
                              lineHeight: 1.4,
                              color: expandida ? azul : texto,
                            }}
                          >
                            {instituicao.name}
                          </Typography>


                        </Box>

                        <Box
                          sx={{
                            width: {
                              xs: "auto",
                              md: 72,
                            },
                            display: "flex",
                            justifyContent: {
                              xs: "flex-start",
                              md: "center",
                            },
                            gridColumn: 2,
                            gridRow: 2,
                            flexShrink: 0,
                          }}
                        >
                          <Responsavel responsavel={responsavel} />
                        </Box>

                        {expandida ? (
                          <KeyboardArrowUpRounded
                            sx={{
                              fontSize: 21,
                              color: azul,
                            }}
                          />
                        ) : (
                          <KeyboardArrowDownRounded
                            sx={{
                              fontSize: 21,
                              color: secundario,
                            }}
                          />
                        )}
                      </ButtonBase>
                    </Tooltip>

                    {/* EXPANSÃO E ROLAGEM DOS PROCESSOS */}
                    <Collapse
                      in={expandida}
                      timeout="auto"
                      unmountOnExit
                      appear
                      onEntered={(elemento) => {
                        const area = elemento.closest("main");

                        if (!area) return;

                        const areaRect = area.getBoundingClientRect();

                        const limiteInferior =
                          Math.min(areaRect.bottom, window.innerHeight) - 12;

                        const processos = Array.from(
                          elemento.querySelectorAll("li"),
                        );

                        const primeiroOculto = processos.find(
                          (processo) =>
                            processo.getBoundingClientRect().bottom >
                            limiteInferior,
                        );

                        if (!primeiroOculto) return;

                        const alturaDisponivel =
                          limiteInferior - Math.max(areaRect.top, 0);

                        const conteudo = elemento.getBoundingClientRect();

                        const alvo =
                          conteudo.height <= alturaDisponivel
                            ? conteudo.bottom
                            : primeiroOculto.getBoundingClientRect().bottom;

                        const deslocamento = Math.max(0, alvo - limiteInferior);

                        if (deslocamento === 0) return;

                        const areaRolavel =
                          area.scrollHeight > area.clientHeight ? area : window;

                        areaRolavel.scrollBy({
                          top: deslocamento,
                          behavior: "smooth",
                        });
                      }}
                    >
                      <Box
                        sx={{
                          borderTop: `1px solid ${borda}`,
                          ml: {
                            xs: 1.5,
                            md: 7,
                          },
                          mr: 1.5,
                          mb: 1,
                        }}
                      >
                        <Box
                          component="ul"
                          aria-label={`Processos de ${instituicao.name}`}
                          sx={{
                            m: 0,
                            p: 0,
                            listStyle: "none",
                          }}
                        >
                          {instituicao.processos.map((processo, indice) => (
                            <Box
                              component="li"
                              key={processo.process_id}
                              sx={{
                                borderTop: indice
                                  ? `1px solid ${borda}`
                                  : "none",
                              }}
                            >
                              <ButtonBase
                                disableRipple
                                data-processo={processo.process_id}
                                aria-label={`Acessar processo ${processo.processo_nome} de ${instituicao.name}`}
                                onClick={() =>
                                  navigate(
                                    `/processo-detalhado/${encodeURIComponent(
                                      instituicao.name,
                                    )}/${processo.process_id}`,
                                  )
                                }
                                sx={{
                                  ...foco,
                                  display: {
                                    xs: "grid",
                                    md: "flex",
                                  },
                                  gridTemplateColumns:
                                    "18px minmax(0,1fr) auto 18px",
                                  alignItems: "center",
                                  width: "100%",
                                  gap: 1.25,
                                  textAlign: "left",
                                  px: 1.5,
                                  py: 1,
                                  color: texto,

                                  "& > svg:first-of-type": {
                                    gridColumn: 1,
                                    gridRow: "1 / 3",
                                  },

                                  "& > div": {
                                    gridColumn: "2 / 4",
                                    gridRow: 1,
                                  },

                                  "& > span:nth-of-type(1)": {
                                    gridColumn: 3,
                                    gridRow: 2,
                                  },

                                  "& > svg:last-of-type": {
                                    gridColumn: 4,
                                    gridRow: "1 / 3",
                                  },

                                  "&:hover": {
                                    bgcolor: suave,
                                    color: azul,
                                  },
                                }}
                              >
                                <DescriptionOutlined
                                  sx={{
                                    fontSize: 18,
                                    color: secundario,
                                    flexShrink: 0,
                                  }}
                                />

                                <Box
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      display: "block",
                                      fontSize: pagina.conteudoTabela,
                                      fontWeight: 500,
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {processo.processo_nome}
                                  </Typography>

                                  {processo.modalidade && (
                                    <Typography
                                      component="span"
                                      sx={{
                                        display: "block",
                                        mt: 0.2,
                                        fontSize: controle.rotulo,
                                        color: secundario,
                                      }}
                                    >
                                      {processo.modalidade}
                                    </Typography>
                                  )}
                                </Box>



                                <Typography
                                  component="span"
                                  sx={{
                                    width: {
                                      xs: "auto",
                                      md: 110,
                                    },
                                    textAlign: {
                                      xs: "left",
                                      md: "right",
                                    },
                                    fontSize: controle.rotulo,
                                    color: secundario,
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                  }}
                                >
                                  {modoAberto ? (
                                    processo.dias_em_aberto != null ? (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          bgcolor: "#2e7d32",
                                          color: "#fff",
                                          borderRadius: "20px",
                                          px: 1.2,
                                          py: 0.4,
                                          fontWeight: 700,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {processo.dias_em_aberto} dia(s)
                                      </Box>
                                    ) : (
                                      ""
                                    )
                                  ) : (
                                    "Finalizado"
                                  )}
                                </Typography>

                                <ChevronRightRounded
                                  aria-hidden="true"
                                  sx={{
                                    fontSize: 19,
                                    color: azul,
                                    flexShrink: 0,
                                  }}
                                />
                              </ButtonBase>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function PaginaProcessos() {
  const { userId } = useContext(CondicaoContext);

  return <ListaProcessos key={userId || "anonimo"} userId={userId} />;
}
